/**
 * ConsensusAnalysisDisplay Component
 *
 * Displays the Chief of Medicine consensus result with the same tabbed interface
 * as Phase 1 model analysis results (ExecutionDetails in TestRunResults).
 */

import { useState } from 'react';
import { clsx } from 'clsx';
import {
  FileText,
  Stethoscope,
  Users,
  Search,
  MessageSquare,
  Lightbulb,
  CheckCircle,
  AlertCircle,
  Crown,
} from 'lucide-react';
import { getModelName } from '../../../components/ModelLogo';

// Normalize priority values from various LLM output formats to standard values
// Handles: P1_CRITICAL → critical, P2_IMPORTANT → high, P3_PREFERRED → medium, etc.
function normalizePriority(priority: string | undefined): 'critical' | 'high' | 'medium' | 'low' {
  if (!priority) return 'medium';
  const p = priority.toLowerCase();
  if (p === 'critical' || p.includes('p1') || p.includes('critical')) return 'critical';
  if (p === 'high' || p.includes('p2') || p.includes('important')) return 'high';
  if (p === 'medium' || p.includes('p3') || p.includes('preferred')) return 'medium';
  if (p === 'low' || p.includes('p4') || p.includes('optional')) return 'low';
  return 'medium'; // default fallback
}

// Type definitions matching the consensus result structure from moderator
interface IndicationIssue {
  severity: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  recommendation?: string;
}

interface PatientExpectations {
  summary: string;
  concerns: string[];
  goals: string[];
}

interface DocumentFinding {
  documentName: string;
  observations: string[];
  clinicalSignificance?: string;
}

interface DocumentInconsistency {
  description: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  documentSource: string;
  questionnaireSource?: string;
}

interface DocumentFindings {
  hasDocuments: boolean;
  hasInconsistencies: boolean;
  findings: DocumentFinding[];
  inconsistencies?: DocumentInconsistency[];
  verificationStatus?: string;
}

interface IndicationOutput {
  suitability: 'SUITABLE' | 'UNSUITABLE' | 'REQUIRES_REVIEW';
  confidence: number;
  summary: string;
  issues: IndicationIssue[];
  patientExpectations?: PatientExpectations;
  documentFindings?: DocumentFindings;
}

interface SearchCriterion {
  id?: string;
  field: string;
  value: string | string[];
  priority: 'critical' | 'high' | 'medium' | 'low';
  reasoning?: string;
  name?: string;
  description?: string;
}

interface CriteriaOutput {
  criteria: SearchCriterion[];
  priorityExplanation?: string;
  additionalNotes?: string;
}

// Phase 2 Search Results types
interface ProviderResult {
  doctorId: string;
  type: 'doctor' | 'institution';
  name: string;
  title?: string;
  institution?: string;
  city: string;
  canton: string;
  specialty: string;
  matchScore: number;
  matchReasons: string[];
  criteriaMatched: string[];
}

interface SearchResultsOutput {
  webResults: ProviderResult[];
  searchSummary?: string;
  topDoctors?: string[];
  topInstitutions?: string[];
  rankingRationale?: string;
}

interface ConsensusResult {
  type?: 'consensus';
  // Phase 1 fields
  indication?: IndicationOutput;
  criteria?: CriteriaOutput;
  // Phase 2 fields
  searchResults?: SearchResultsOutput;
  // Common fields
  reasoning?: string;
  confidenceScore?: number;
  supportingModels?: string[];
  minorReservations?: string[];
}

interface ConsensusAnalysisDisplayProps {
  result: ConsensusResult;
  outcomeType?: string;
}

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-gray-100 text-gray-600',
};

const PRIORITY_COLORS: Record<string, string> = {
  critical: 'bg-red-100 text-red-700 border-red-200',
  high: 'bg-orange-100 text-orange-700 border-orange-200',
  medium: 'bg-amber-100 text-amber-700 border-amber-200',
  low: 'bg-gray-100 text-gray-600 border-gray-200',
};

export function ConsensusAnalysisDisplay({ result, outcomeType }: ConsensusAnalysisDisplayProps) {
  const [activeTab, setActiveTab] = useState<'summary' | 'assessment' | 'expectations' | 'criteria' | 'documents' | 'providers' | 'ranking'>('summary');

  const indication = result.indication;
  const criteria = result.criteria;
  const searchResults = result.searchResults;
  const documentFindings = indication?.documentFindings;

  // Detect if this is Phase 2 (has search results but no indication)
  const isPhase2 = !!searchResults && !indication;

  // If no structured data, show simple display
  if (!indication && !criteria && !searchResults) {
    return (
      <div className="space-y-4">
        {result.reasoning && (
          <div>
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Moderator Reasoning</h4>
            <p className="text-sm text-gray-700 leading-relaxed">{result.reasoning}</p>
          </div>
        )}
        {result.supportingModels && result.supportingModels.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Supporting Experts</h4>
            <div className="flex flex-wrap gap-2">
              {result.supportingModels.map((model, idx) => (
                <span key={idx} className="px-2 py-1 rounded-full text-xs bg-indigo-100 text-indigo-700">
                  {getModelName(model)}
                </span>
              ))}
            </div>
          </div>
        )}
        {result.minorReservations && result.minorReservations.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Minor Reservations</h4>
            <ul className="list-disc list-inside space-y-1">
              {result.minorReservations.map((res, idx) => (
                <li key={idx} className="text-sm text-gray-600">{res}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Consensus Header */}
      {outcomeType && (
        <div className={clsx(
          'px-4 py-2 flex items-center gap-2',
          outcomeType === 'FULL_CONSENSUS' ? 'bg-green-50 border-b border-green-200' :
          outcomeType === 'MAJORITY_CONSENSUS' ? 'bg-blue-50 border-b border-blue-200' :
          outcomeType === 'PARTIAL_CONSENSUS' ? 'bg-amber-50 border-b border-amber-200' :
          'bg-purple-50 border-b border-purple-200'
        )}>
          <Crown className={clsx(
            'h-4 w-4',
            outcomeType === 'FULL_CONSENSUS' ? 'text-green-600' :
            outcomeType === 'MAJORITY_CONSENSUS' ? 'text-blue-600' :
            outcomeType === 'PARTIAL_CONSENSUS' ? 'text-amber-600' :
            'text-purple-600'
          )} />
          <span className={clsx(
            'text-sm font-medium',
            outcomeType === 'FULL_CONSENSUS' ? 'text-green-700' :
            outcomeType === 'MAJORITY_CONSENSUS' ? 'text-blue-700' :
            outcomeType === 'PARTIAL_CONSENSUS' ? 'text-amber-700' :
            'text-purple-700'
          )}>
            Chief of Medicine Synthesis
          </span>
          {result.confidenceScore !== undefined && (
            <span className="ml-auto text-xs text-gray-500">
              {result.confidenceScore}% confidence
            </span>
          )}
        </div>
      )}

      {/* Tabs - Different for Phase 1 vs Phase 2 */}
      <div className="flex border-b border-gray-200 bg-gray-50 overflow-x-auto">
        <button
          onClick={() => setActiveTab('summary')}
          className={clsx(
            'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap',
            activeTab === 'summary'
              ? 'border-indigo-500 text-indigo-700 bg-white'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          )}
        >
          <FileText className="h-4 w-4" />
          Summary
        </button>

        {/* Phase 1 specific tabs */}
        {!isPhase2 && (
          <>
            <button
              onClick={() => setActiveTab('assessment')}
              className={clsx(
                'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap',
                activeTab === 'assessment'
                  ? 'border-indigo-500 text-indigo-700 bg-white'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              )}
            >
              <Stethoscope className="h-4 w-4" />
              Medical Assessment
            </button>
            <button
              onClick={() => setActiveTab('expectations')}
              className={clsx(
                'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap',
                activeTab === 'expectations'
                  ? 'border-indigo-500 text-indigo-700 bg-white'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              )}
            >
              <Users className="h-4 w-4" />
              Patient Expectations
            </button>
            <button
              onClick={() => setActiveTab('criteria')}
              className={clsx(
                'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap',
                activeTab === 'criteria'
                  ? 'border-indigo-500 text-indigo-700 bg-white'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              )}
            >
              <Search className="h-4 w-4" />
              Matching Criteria
            </button>
            {documentFindings?.hasDocuments && (
              <button
                onClick={() => setActiveTab('documents')}
                className={clsx(
                  'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap',
                  activeTab === 'documents'
                    ? 'border-indigo-500 text-indigo-700 bg-white'
                    : 'border-transparent text-gray-500 hover:text-gray-700',
                  documentFindings.hasInconsistencies && 'text-red-600'
                )}
              >
                <FileText className="h-4 w-4" />
                Documents
                {documentFindings.hasInconsistencies && (
                  <span className="ml-1 px-1.5 py-0.5 text-xs bg-red-100 text-red-700 rounded-full">!</span>
                )}
              </button>
            )}
          </>
        )}

        {/* Phase 2 specific tabs */}
        {isPhase2 && (
          <>
            <button
              onClick={() => setActiveTab('providers')}
              className={clsx(
                'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap',
                activeTab === 'providers'
                  ? 'border-indigo-500 text-indigo-700 bg-white'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              )}
            >
              <Users className="h-4 w-4" />
              Provider Recommendations
              {searchResults?.webResults && (
                <span className="ml-1 px-1.5 py-0.5 text-xs bg-indigo-100 text-indigo-700 rounded-full">
                  {searchResults.webResults.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('ranking')}
              className={clsx(
                'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap',
                activeTab === 'ranking'
                  ? 'border-indigo-500 text-indigo-700 bg-white'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              )}
            >
              <Stethoscope className="h-4 w-4" />
              Ranking Rationale
            </button>
          </>
        )}
      </div>

      {/* Tab Content */}
      <div className="p-4 bg-white">
        {/* Summary Tab - Phase 1 */}
        {activeTab === 'summary' && !isPhase2 && indication && (
          <div className="space-y-4">
            {/* Key Points */}
            <div>
              <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Key Points</h4>
              <div className="flex flex-wrap gap-2">
                <span className={clsx(
                  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium',
                  indication.suitability === 'SUITABLE'
                    ? 'bg-green-100 text-green-700'
                    : indication.suitability === 'UNSUITABLE'
                    ? 'bg-red-100 text-red-700'
                    : 'bg-amber-100 text-amber-700'
                )}>
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white text-xs font-bold">1</span>
                  {indication.suitability.replace('_', ' ')}
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-blue-100 text-blue-700">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white text-xs font-bold">2</span>
                  {indication.confidence}% Confidence
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-gray-100 text-gray-700">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white text-xs font-bold">3</span>
                  {indication.issues.length} Issues
                </span>
                {criteria?.criteria && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-purple-100 text-purple-700">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white text-xs font-bold">4</span>
                    {criteria.criteria.length} Criteria
                  </span>
                )}
              </div>
            </div>

            {/* Summary */}
            <div>
              <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Case Summary</h4>
              <p className="text-sm text-gray-700 leading-relaxed">{indication.summary}</p>
            </div>

            {/* Consensus Reasoning */}
            {result.reasoning && (
              <div className="p-3 rounded-lg bg-indigo-50 border border-indigo-200">
                <h4 className="text-xs font-semibold text-indigo-700 uppercase tracking-wide mb-2">
                  Consensus Reasoning
                </h4>
                <p className="text-sm text-indigo-800">{result.reasoning}</p>
              </div>
            )}

            {/* Supporting Experts */}
            {result.supportingModels && result.supportingModels.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                  Supporting Experts
                </h4>
                <div className="flex flex-wrap gap-2">
                  {result.supportingModels.map((model, idx) => (
                    <span key={idx} className="px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                      {getModelName(model)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Issues Preview */}
            {indication.issues.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Issues Overview</h4>
                <div className="space-y-2">
                  {indication.issues.slice(0, 3).map((issue, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <span className={clsx(
                        'px-2 py-0.5 rounded text-xs font-medium capitalize',
                        SEVERITY_COLORS[issue.severity] || 'bg-gray-100 text-gray-600'
                      )}>
                        {issue.severity}
                      </span>
                      <span className="text-sm text-gray-600">{issue.description}</span>
                    </div>
                  ))}
                  {indication.issues.length > 3 && (
                    <p className="text-xs text-gray-500">
                      + {indication.issues.length - 3} more issues (see Medical Assessment tab)
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Minor Reservations */}
            {result.minorReservations && result.minorReservations.length > 0 && (
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                <h4 className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-2">
                  Minor Reservations
                </h4>
                <ul className="list-disc list-inside space-y-1">
                  {result.minorReservations.map((res, idx) => (
                    <li key={idx} className="text-sm text-amber-800">{res}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Medical Assessment Tab */}
        {activeTab === 'assessment' && indication && (
          <div className="space-y-4">
            {/* Suitability Decision */}
            <div className="p-4 rounded-lg bg-gray-50">
              <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Stated Indication</h4>
              <div className="flex items-center gap-3">
                <span className={clsx(
                  'px-3 py-1.5 rounded-lg text-sm font-semibold',
                  indication.suitability === 'SUITABLE'
                    ? 'bg-green-100 text-green-700'
                    : indication.suitability === 'UNSUITABLE'
                    ? 'bg-red-100 text-red-700'
                    : 'bg-amber-100 text-amber-700'
                )}>
                  {indication.suitability.replace('_', ' ')}
                </span>
                <span className="text-sm text-gray-500">
                  with {indication.confidence}% confidence
                </span>
              </div>
            </div>

            {/* All Issues */}
            {indication.issues.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">
                  All Issues ({indication.issues.length})
                </h4>
                <div className="space-y-3">
                  {indication.issues.map((issue, idx) => (
                    <div key={idx} className="p-3 rounded-lg border border-gray-200 bg-white">
                      <div className="flex items-start gap-3">
                        <span className={clsx(
                          'px-2 py-0.5 rounded text-xs font-medium capitalize shrink-0',
                          SEVERITY_COLORS[issue.severity] || 'bg-gray-100 text-gray-600'
                        )}>
                          {issue.severity}
                        </span>
                        <div className="flex-1">
                          <p className="text-sm text-gray-700">{issue.description}</p>
                          {issue.recommendation && (
                            <p className="mt-2 text-xs text-gray-500 flex items-start gap-1">
                              <Lightbulb className="h-3 w-3 mt-0.5 shrink-0" />
                              {issue.recommendation}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {indication.issues.length === 0 && (
              <div className="p-4 rounded-lg bg-green-50 text-center">
                <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                <p className="text-sm text-green-700">No issues identified</p>
              </div>
            )}
          </div>
        )}

        {/* Patient Expectations Tab */}
        {activeTab === 'expectations' && indication && (
          <div className="space-y-4">
            {indication.patientExpectations ? (
              <>
                {/* Expectations Summary */}
                <div className="p-4 rounded-lg bg-gray-50">
                  <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                    Expectation Assessment
                  </h4>
                  <p className="text-sm text-gray-700">{indication.patientExpectations.summary}</p>
                </div>

                {/* Communication Needs / Concerns */}
                {indication.patientExpectations.concerns && indication.patientExpectations.concerns.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2 flex items-center gap-2">
                      <MessageSquare className="h-4 w-4" />
                      Communication Needs
                    </h4>
                    <ul className="space-y-2">
                      {indication.patientExpectations.concerns.map((concern, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                          <span className="text-amber-500 mt-1">•</span>
                          {concern}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Goals / Ideal Provider Traits */}
                {indication.patientExpectations.goals && indication.patientExpectations.goals.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2 flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Ideal Provider Traits
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {indication.patientExpectations.goals.map((goal, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm bg-blue-50 text-blue-700"
                        >
                          {goal}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="p-4 rounded-lg bg-gray-50 text-center">
                <Users className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No patient expectations data available</p>
              </div>
            )}
          </div>
        )}

        {/* Matching Criteria Tab */}
        {activeTab === 'criteria' && (
          <div className="space-y-4">
            {criteria?.criteria && criteria.criteria.length > 0 ? (
              <>
                {/* Priority Explanation */}
                {criteria.priorityExplanation && (
                  <div className="p-4 rounded-lg bg-gray-50">
                    <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                      Priority Rationale
                    </h4>
                    <p className="text-sm text-gray-700">{criteria.priorityExplanation}</p>
                  </div>
                )}

                {/* Additional Notes */}
                {criteria.additionalNotes && (
                  <div className="p-3 rounded-lg bg-indigo-50 border border-indigo-200">
                    <h4 className="text-xs font-semibold text-indigo-700 uppercase tracking-wide mb-1">
                      Additional Notes
                    </h4>
                    <p className="text-sm text-indigo-800">{criteria.additionalNotes}</p>
                  </div>
                )}

                {/* Matching Criteria by Priority */}
                {(['critical', 'high', 'medium', 'low'] as const).map(priority => {
                  const priorityCriteria = criteria.criteria.filter(
                    c => normalizePriority(c.priority) === priority
                  );
                  if (priorityCriteria.length === 0) return null;

                  return (
                    <div key={priority}>
                      <h4 className={clsx(
                        'text-xs font-semibold uppercase tracking-wide mb-2 flex items-center gap-2',
                        priority === 'critical' ? 'text-red-700' :
                        priority === 'high' ? 'text-orange-700' :
                        priority === 'medium' ? 'text-amber-700' : 'text-gray-600'
                      )}>
                        <span className={clsx(
                          'w-2 h-2 rounded-full',
                          priority === 'critical' ? 'bg-red-500' :
                          priority === 'high' ? 'bg-orange-500' :
                          priority === 'medium' ? 'bg-amber-500' : 'bg-gray-400'
                        )} />
                        {priority.charAt(0).toUpperCase() + priority.slice(1)} Priority ({priorityCriteria.length})
                      </h4>
                      <div className="space-y-2">
                        {priorityCriteria.map((criterion, idx) => (
                          <div
                            key={idx}
                            className={clsx(
                              'p-3 rounded-lg border',
                              PRIORITY_COLORS[priority] || 'border-gray-200 bg-white'
                            )}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1">
                                <p className="font-medium text-gray-900">
                                  {criterion.name || criterion.field}
                                </p>
                                <p className="mt-1 text-sm text-gray-600">
                                  {Array.isArray(criterion.value)
                                    ? criterion.value.join(', ')
                                    : criterion.value}
                                </p>
                                {criterion.reasoning && (
                                  <p className="mt-2 text-xs text-gray-500">
                                    {criterion.reasoning}
                                  </p>
                                )}
                                {criterion.description && !criterion.reasoning && (
                                  <p className="mt-2 text-xs text-gray-500">
                                    {criterion.description}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </>
            ) : (
              <div className="p-4 rounded-lg bg-gray-50 text-center">
                <Search className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No matching criteria generated</p>
              </div>
            )}
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && documentFindings && (
          <div className="space-y-4">
            {/* Verification Status */}
            {documentFindings.verificationStatus && (
              <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                <h4 className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2">
                  Verification Status
                </h4>
                <p className="text-sm text-blue-700">{documentFindings.verificationStatus}</p>
              </div>
            )}

            {/* Inconsistencies Warning */}
            {documentFindings.hasInconsistencies && documentFindings.inconsistencies && documentFindings.inconsistencies.length > 0 && (
              <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                <h4 className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  Inconsistencies Detected ({documentFindings.inconsistencies.length})
                </h4>
                <div className="space-y-3">
                  {documentFindings.inconsistencies.map((inconsistency, idx) => {
                    // Handle potential field name variations from LLM output
                    const rawInconsistency = inconsistency as unknown as Record<string, unknown>;
                    const severity = (rawInconsistency.severity as string) || 'medium';
                    const description = (rawInconsistency.description as string) ||
                                        (rawInconsistency.issue as string) ||
                                        (rawInconsistency.text as string) ||
                                        JSON.stringify(rawInconsistency);
                    const documentSource = (rawInconsistency.documentSource as string) ||
                                          (rawInconsistency.document as string) ||
                                          (rawInconsistency.source as string) ||
                                          '';
                    const questionnaireSource = (rawInconsistency.questionnaireSource as string) ||
                                               (rawInconsistency.questionnaire as string) ||
                                               (rawInconsistency.question as string);

                    return (
                      <div key={idx} className="p-3 rounded-lg bg-white border border-red-200">
                        <div className="flex items-start gap-3">
                          <span className={clsx(
                            'px-2 py-0.5 rounded text-xs font-medium capitalize shrink-0',
                            SEVERITY_COLORS[severity] || 'bg-gray-100 text-gray-600'
                          )}>
                            {severity || 'issue'}
                          </span>
                          <div className="flex-1">
                            <p className="text-sm text-gray-700">{description}</p>
                            {documentSource && (
                              <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                                <span>
                                  <strong>Document:</strong> {documentSource}
                                </span>
                                {questionnaireSource && (
                                  <span>
                                    <strong>Question:</strong> {questionnaireSource}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Document Findings */}
            {documentFindings.findings && documentFindings.findings.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">
                  Document Analysis ({documentFindings.findings.length})
                </h4>
                <div className="space-y-3">
                  {documentFindings.findings.map((finding, idx) => {
                    // Handle potential field name variations from LLM output
                    const rawFinding = finding as unknown as Record<string, unknown>;
                    const documentName = (rawFinding.documentName as string) ||
                                        (rawFinding.name as string) ||
                                        (rawFinding.document as string) ||
                                        (rawFinding.fileName as string) ||
                                        `Document ${idx + 1}`;

                    // Observations can be array or single value
                    let observations: string[] = [];
                    if (Array.isArray(rawFinding.observations)) {
                      observations = rawFinding.observations as string[];
                    } else if (typeof rawFinding.observations === 'string') {
                      observations = [rawFinding.observations];
                    } else if (Array.isArray(rawFinding.findings)) {
                      observations = rawFinding.findings as string[];
                    } else if (typeof rawFinding.findings === 'string') {
                      observations = [rawFinding.findings];
                    } else if (rawFinding.summary) {
                      observations = [rawFinding.summary as string];
                    }

                    const clinicalSignificance = (rawFinding.clinicalSignificance as string) ||
                                                (rawFinding.significance as string) ||
                                                (rawFinding.clinical_significance as string);

                    return (
                      <div key={idx} className="p-4 rounded-lg border border-gray-200 bg-white">
                        <div className="flex items-start gap-3">
                          <FileText className="h-5 w-5 text-gray-400 shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <h5 className="font-medium text-gray-900 mb-2">{documentName}</h5>

                            {observations.length > 0 && (
                              <div className="mb-3">
                                <p className="text-xs font-medium text-gray-500 mb-1">Observations:</p>
                                <ul className="list-disc list-inside space-y-1">
                                  {observations.map((obs, obsIdx) => (
                                    <li key={obsIdx} className="text-sm text-gray-700">{obs}</li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {clinicalSignificance && (
                              <div className="p-2 rounded bg-amber-50 border border-amber-200">
                                <p className="text-xs font-medium text-amber-700 mb-1">Clinical Significance:</p>
                                <p className="text-sm text-amber-900">{clinicalSignificance}</p>
                              </div>
                            )}

                            {/* Fallback: show raw data if no standard fields found */}
                            {observations.length === 0 && !clinicalSignificance && (
                              <pre className="text-xs text-gray-500 bg-gray-50 p-2 rounded overflow-x-auto">
                                {JSON.stringify(rawFinding, null, 2)}
                              </pre>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* No Findings */}
            {(!documentFindings.findings || documentFindings.findings.length === 0) &&
             (!documentFindings.inconsistencies || documentFindings.inconsistencies.length === 0) && (
              <div className="p-4 rounded-lg bg-gray-50 text-center">
                <FileText className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Documents were attached but no analysis findings are available</p>
              </div>
            )}
          </div>
        )}

        {/* Phase 2: Provider Recommendations Tab */}
        {activeTab === 'providers' && searchResults && (
          <div className="space-y-4">
            {/* Top Recommendations */}
            {(searchResults.topDoctors?.length || searchResults.topInstitutions?.length) && (
              <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                <h4 className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-3">
                  Top Recommendations
                </h4>
                <div className="space-y-3">
                  {searchResults.topDoctors && searchResults.topDoctors.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-green-600 mb-1">Top Care Providers:</p>
                      <div className="flex flex-wrap gap-2">
                        {searchResults.topDoctors.map((doctor, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-white text-green-700 border border-green-200"
                          >
                            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-green-100 text-xs font-bold">
                              {idx + 1}
                            </span>
                            {doctor}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {searchResults.topInstitutions && searchResults.topInstitutions.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-green-600 mb-1">Top Institutions:</p>
                      <div className="flex flex-wrap gap-2">
                        {searchResults.topInstitutions.map((inst, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-white text-green-700 border border-green-200"
                          >
                            {inst}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Provider List */}
            {searchResults.webResults && searchResults.webResults.length > 0 ? (
              <div>
                <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">
                  All Recommended Providers ({searchResults.webResults.length})
                </h4>
                <div className="space-y-3">
                  {searchResults.webResults.map((provider, idx) => (
                    <ProviderCard key={provider.doctorId || idx} provider={provider} rank={idx + 1} />
                  ))}
                </div>
              </div>
            ) : (
              <div className="p-4 rounded-lg bg-gray-50 text-center">
                <Users className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No provider recommendations available</p>
              </div>
            )}
          </div>
        )}

        {/* Phase 2: Ranking Rationale Tab */}
        {activeTab === 'ranking' && searchResults && (
          <div className="space-y-4">
            {/* Search Summary */}
            {searchResults.searchSummary && (
              <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                <h4 className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2">
                  Search Summary
                </h4>
                <p className="text-sm text-blue-800">{searchResults.searchSummary}</p>
              </div>
            )}

            {/* Ranking Rationale */}
            {searchResults.rankingRationale && (
              <div className="p-4 rounded-lg bg-gray-50">
                <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                  Ranking Rationale
                </h4>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{searchResults.rankingRationale}</p>
              </div>
            )}

            {/* Consensus Reasoning */}
            {result.reasoning && (
              <div className="p-3 rounded-lg bg-indigo-50 border border-indigo-200">
                <h4 className="text-xs font-semibold text-indigo-700 uppercase tracking-wide mb-2">
                  Consensus Reasoning
                </h4>
                <p className="text-sm text-indigo-800 whitespace-pre-wrap">{result.reasoning}</p>
              </div>
            )}

            {/* Supporting Experts */}
            {result.supportingModels && result.supportingModels.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                  Supporting Experts
                </h4>
                <div className="flex flex-wrap gap-2">
                  {result.supportingModels.map((model, idx) => (
                    <span key={idx} className="px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                      {getModelName(model)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Minor Reservations */}
            {result.minorReservations && result.minorReservations.length > 0 && (
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                <h4 className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-2">
                  Minor Reservations
                </h4>
                <ul className="list-disc list-inside space-y-1">
                  {result.minorReservations.map((res, idx) => (
                    <li key={idx} className="text-sm text-amber-800">{res}</li>
                  ))}
                </ul>
              </div>
            )}

            {!searchResults.searchSummary && !searchResults.rankingRationale && !result.reasoning && (
              <div className="p-4 rounded-lg bg-gray-50 text-center">
                <Stethoscope className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No ranking rationale available</p>
              </div>
            )}
          </div>
        )}

        {/* Phase 2 Summary Tab */}
        {activeTab === 'summary' && isPhase2 && searchResults && (
          <div className="space-y-4">
            {/* Key Points */}
            <div>
              <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Key Points</h4>
              <div className="flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-green-100 text-green-700">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white text-xs font-bold">1</span>
                  {searchResults.webResults?.length || 0} Providers
                </span>
                {result.confidenceScore !== undefined && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-blue-100 text-blue-700">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white text-xs font-bold">2</span>
                    {result.confidenceScore}% Confidence
                  </span>
                )}
                {searchResults.topDoctors && searchResults.topDoctors.length > 0 && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-purple-100 text-purple-700">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white text-xs font-bold">3</span>
                    {searchResults.topDoctors.length} Top Care Providers
                  </span>
                )}
              </div>
            </div>

            {/* Search Summary */}
            {searchResults.searchSummary && (
              <div>
                <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Matching Summary</h4>
                <p className="text-sm text-gray-700 leading-relaxed">{searchResults.searchSummary}</p>
              </div>
            )}

            {/* Top Recommendations Preview */}
            {searchResults.topDoctors && searchResults.topDoctors.length > 0 && (
              <div className="p-3 rounded-lg bg-green-50 border border-green-200">
                <h4 className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-2">
                  Top Recommended Care Providers
                </h4>
                <div className="flex flex-wrap gap-2">
                  {searchResults.topDoctors.slice(0, 5).map((doctor, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm bg-white text-green-700 border border-green-200"
                    >
                      <span className="font-bold text-xs">{idx + 1}.</span>
                      {doctor}
                    </span>
                  ))}
                  {searchResults.topDoctors.length > 5 && (
                    <span className="text-xs text-green-600">
                      + {searchResults.topDoctors.length - 5} more
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Consensus Reasoning */}
            {result.reasoning && (
              <div className="p-3 rounded-lg bg-indigo-50 border border-indigo-200">
                <h4 className="text-xs font-semibold text-indigo-700 uppercase tracking-wide mb-2">
                  Consensus Reasoning
                </h4>
                <p className="text-sm text-indigo-800">{result.reasoning}</p>
              </div>
            )}

            {/* Supporting Experts */}
            {result.supportingModels && result.supportingModels.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                  Supporting Experts
                </h4>
                <div className="flex flex-wrap gap-2">
                  {result.supportingModels.map((model, idx) => (
                    <span key={idx} className="px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                      {getModelName(model)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Minor Reservations */}
            {result.minorReservations && result.minorReservations.length > 0 && (
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                <h4 className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-2">
                  Minor Reservations
                </h4>
                <ul className="list-disc list-inside space-y-1">
                  {result.minorReservations.map((res, idx) => (
                    <li key={idx} className="text-sm text-amber-800">{res}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Provider Card Component for Phase 2
 */
function ProviderCard({ provider, rank }: { provider: ProviderResult; rank: number }) {
  return (
    <div className="p-4 rounded-lg border border-gray-200 bg-white hover:border-gray-300 transition-colors">
      <div className="flex items-start gap-4">
        {/* Rank Badge */}
        <div className="flex-shrink-0">
          <span className={clsx(
            'flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold',
            rank <= 3 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
          )}>
            {rank}
          </span>
        </div>

        {/* Provider Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h5 className="font-medium text-gray-900 truncate">{provider.name}</h5>
            {provider.title && (
              <span className="text-xs text-gray-500">({provider.title})</span>
            )}
            <span className={clsx(
              'px-2 py-0.5 rounded text-xs font-medium',
              provider.type === 'doctor' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
            )}>
              {provider.type === 'doctor' ? 'Doctor' : 'Institution'}
            </span>
          </div>

          {provider.institution && (
            <p className="text-sm text-gray-600 mb-1">{provider.institution}</p>
          )}

          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 mb-2">
            <span>{provider.specialty}</span>
            <span>•</span>
            <span>{provider.city}, {provider.canton}</span>
          </div>

          {/* Match Score */}
          <div className="flex items-center gap-2 mb-2">
            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={clsx(
                  'h-full rounded-full',
                  provider.matchScore >= 80 ? 'bg-green-500' :
                  provider.matchScore >= 60 ? 'bg-amber-500' : 'bg-red-500'
                )}
                style={{ width: `${provider.matchScore}%` }}
              />
            </div>
            <span className={clsx(
              'text-sm font-medium',
              provider.matchScore >= 80 ? 'text-green-700' :
              provider.matchScore >= 60 ? 'text-amber-700' : 'text-red-700'
            )}>
              {provider.matchScore}%
            </span>
          </div>

          {/* Match Reasons */}
          {provider.matchReasons && provider.matchReasons.length > 0 && (
            <div className="mb-2">
              <p className="text-xs font-medium text-gray-500 mb-1">Why recommended:</p>
              <ul className="list-disc list-inside space-y-0.5">
                {provider.matchReasons.map((reason, idx) => (
                  <li key={idx} className="text-xs text-gray-600">{reason}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Matching Criteria Matched */}
          {provider.criteriaMatched && provider.criteriaMatched.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {provider.criteriaMatched.map((criterion, idx) => (
                <span key={idx} className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600">
                  {criterion}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
